import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Wrench, UsersRound, Briefcase, Receipt, Loader2, History, X, FileText, Package } from "lucide-react";
import { api } from "../lib/api";
import { useYacht } from "../context/YachtContext";
import {
  Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem,
} from "./ui/command";
import { Dialog, DialogContent } from "./ui/dialog";

// Module-level opener so any trigger (sidebar button, mobile topbar) can
// open the palette without prop drilling.
const EVT = "seavanto:open-global-search";
export const openGlobalSearch = () => window.dispatchEvent(new Event(EVT));

const SENIOR = new Set(["owner", "manager", "captain", "first_officer"]);
const EMPTY = { tasks: [], crew: [], suppliers: [], invoices: [], documents: [], inventory: [] };

const RECENTS_KEY = "seavanto:recent-searches";
const loadRecents = () => {
  try { return JSON.parse(localStorage.getItem(RECENTS_KEY)) || []; } catch { return []; }
};

export const GlobalSearch = () => {
  const { activeYacht } = useYacht();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [recents, setRecents] = useState(loadRecents);
  const timer = useRef(null);

  const saveRecents = (list) => {
    setRecents(list);
    try { localStorage.setItem(RECENTS_KEY, JSON.stringify(list)); } catch { /* storage full/blocked */ }
  };
  const rememberSearch = (term) => {
    if (!term || term.length < 2) return;
    saveRecents([term, ...recents.filter((r) => r.toLowerCase() !== term.toLowerCase())].slice(0, 6));
  };
  const removeRecent = (term) => saveRecents(recents.filter((r) => r !== term));

  useEffect(() => {
    const onOpen = () => setOpen(true);
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    window.addEventListener(EVT, onOpen);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener(EVT, onOpen);
      window.removeEventListener("keydown", onKey);
    };
  }, []);

  useEffect(() => {
    if (!open) { setQ(""); setResults(EMPTY); }
  }, [open]);

  useEffect(() => {
    clearTimeout(timer.current);
    const term = q.trim();
    if (!activeYacht?.id || term.length < 2) { setResults(EMPTY); setBusy(false); return; }
    setBusy(true);
    timer.current = setTimeout(async () => {
      try {
        const { data } = await api.get("/search", { params: { yacht_id: activeYacht.id, q: term } });
        setResults(data);
      } catch {
        setResults(EMPTY);
      } finally {
        setBusy(false);
      }
    }, 250);
    return () => clearTimeout(timer.current);
  }, [q, activeYacht?.id]);

  const go = useCallback((path) => {
    rememberSearch(q.trim());
    setOpen(false);
    navigate(path);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, q, recents]);

  const senior = SENIOR.has(results.my_role || activeYacht?.my_role);
  const hasAny = results.tasks?.length || results.crew?.length || results.suppliers?.length
    || results.invoices?.length || results.documents?.length || results.inventory?.length;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="overflow-hidden p-0" data-testid="global-search-dialog">
        <Command
          shouldFilter={false}
          className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground [&_[cmdk-group]]:px-2 [&_[cmdk-input]]:h-12 [&_[cmdk-item]]:px-2 [&_[cmdk-item]]:py-3 [&_[cmdk-item]_svg]:h-4 [&_[cmdk-item]_svg]:w-4 [&_[cmdk-item]_svg]:mr-2"
        >
      <CommandInput
        placeholder={`Search ${activeYacht?.name || "yacht"}… (tasks, crew, suppliers, invoices, docs, inventory)`}
        value={q}
        onValueChange={setQ}
        data-testid="global-search-input"
      />
      <CommandList data-testid="global-search-results">
        {busy && (
          <div className="flex items-center gap-2 px-4 py-3 text-sm text-zinc-500">
            <Loader2 className="h-4 w-4 animate-spin" /> Searching…
          </div>
        )}
        {!busy && q.trim().length >= 2 && !hasAny && (
          <CommandEmpty data-testid="global-search-empty">No results for “{q.trim()}”</CommandEmpty>
        )}
        {!busy && q.trim().length < 2 && recents.length > 0 && (
          <CommandGroup heading="Recent searches">
            {recents.map((r) => (
              <CommandItem
                key={r}
                value={`recent-${r}`}
                onSelect={() => setQ(r)}
                data-testid={`recent-search-item-${r}`}
                className="group"
              >
                <History className="text-zinc-400" />
                <span className="truncate">{r}</span>
                <button
                  onClick={(e) => { e.stopPropagation(); removeRecent(r); }}
                  className="ml-auto rounded p-1 text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100"
                  aria-label={`Remove "${r}" from recent searches`}
                  data-testid={`recent-search-delete-${r}`}
                >
                  <X />
                </button>
              </CommandItem>
            ))}
          </CommandGroup>
        )}
        {!busy && q.trim().length < 2 && recents.length === 0 && (
          <div className="px-4 py-6 text-center text-sm text-zinc-500">
            Type at least 2 characters to search
          </div>
        )}
        {results.tasks?.length > 0 && (
          <CommandGroup heading="Tasks">
            {results.tasks.map((t) => (
              <CommandItem
                key={t.id}
                value={`task-${t.id}`}
                onSelect={() => go(t.routine_id ? `/app/maintenance?open=${t.id}` : `/app/tasks?open=${t.id}`)}
                data-testid={`search-result-task-${t.id}`}
              >
                <Wrench className="text-zinc-400" />
                <span className="truncate">{t.title}</span>
                <span className="ml-auto text-xs text-zinc-400 capitalize shrink-0">{(t.status || "").replaceAll("_", " ")}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}
        {results.crew?.length > 0 && (
          <CommandGroup heading="Crew">
            {results.crew.map((c) => (
              <CommandItem
                key={c.user_id}
                value={`crew-${c.user_id}`}
                onSelect={() => go(senior ? `/app/crew-hr/${c.user_id}?yacht_id=${activeYacht.id}` : "/app/crew")}
                data-testid={`search-result-crew-${c.user_id}`}
              >
                <UsersRound className="text-zinc-400" />
                <span className="truncate">{c.name || c.email}</span>
                <span className="ml-auto text-xs text-zinc-400 capitalize shrink-0">{(c.role || "").replaceAll("_", " ")}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}
        {results.suppliers?.length > 0 && (
          <CommandGroup heading="Suppliers">
            {results.suppliers.map((s) => (
              <CommandItem
                key={s.id}
                value={`supplier-${s.id}`}
                onSelect={() => go(`/app/crew?view=network&q=${encodeURIComponent(s.name || "")}`)}
                data-testid={`search-result-supplier-${s.id}`}
              >
                <Briefcase className="text-zinc-400" />
                <span className="truncate">{s.name}</span>
                {s.category && <span className="ml-auto text-xs text-zinc-400 shrink-0">{s.category}</span>}
              </CommandItem>
            ))}
          </CommandGroup>
        )}
        {results.invoices?.length > 0 && (
          <CommandGroup heading="Invoices">
            {results.invoices.map((i) => (
              <CommandItem
                key={i.id}
                value={`invoice-${i.id}`}
                onSelect={() => go(`/app/invoices?open=${i.id}`)}
                data-testid={`search-result-invoice-${i.id}`}
              >
                <Receipt className="text-zinc-400" />
                <span className="truncate">{i.supplier_name}{i.invoice_number ? ` · ${i.invoice_number}` : ""}</span>
                <span className="ml-auto text-xs text-zinc-400 shrink-0">
                  {Number(i.amount || 0).toLocaleString()} {i.currency}
                </span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}
        {results.documents?.length > 0 && (
          <CommandGroup heading="Documents">
            {results.documents.map((d) => (
              <CommandItem
                key={d.id}
                value={`document-${d.id}`}
                onSelect={() => go(`/app/documents?open=${d.id}`)}
                data-testid={`search-result-document-${d.id}`}
              >
                <FileText className="text-zinc-400" />
                <span className="truncate">{d.name}</span>
                <span className="ml-auto text-xs text-zinc-400 shrink-0">
                  {d.expires_at ? `exp ${String(d.expires_at).slice(0, 10)}` : (d.category || "")}
                </span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}
        {results.inventory?.length > 0 && (
          <CommandGroup heading="Inventory">
            {results.inventory.map((p) => (
              <CommandItem
                key={p.id}
                value={`inventory-${p.id}`}
                onSelect={() => go(`/app/inventory?q=${encodeURIComponent(p.name || "")}`)}
                data-testid={`search-result-inventory-${p.id}`}
              >
                <Package className="text-zinc-400" />
                <span className="truncate">{p.name}{p.part_number ? ` · ${p.part_number}` : ""}</span>
                <span className="ml-auto text-xs text-zinc-400 shrink-0">qty {Number(p.quantity || 0)}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}
      </CommandList>
        </Command>
      </DialogContent>
    </Dialog>
  );
};
